import type {
  AgentAskResponse,
  RmaManifest,
  Schedule,
  ScheduleCreateRequest,
  ScheduleListResponse,
  ApiKey,
  ApiKeyCreated,
  ApiKeyListResponse,
  Approval,
  ApprovalListResponse,
  AuditLogResponse,
  CrawlStatus,
  HarnessStatus,
  User,
  UserCreateRequest,
  CaseBatchIntakeRequest,
  CaseBatchIntakeResponse,
  CaseDeleteResponse,
  CaseSymptomsResponse,
  Evidence,
  EvidenceListResponse,
  EvidenceUploadMeta,
  MailDraft,
  MailType,
  ShipmentBatchRequest,
  ShipmentBatchResponse,
  SwapCreateRequest,
  CaseDetail,
  CaseListResponse,
  CaseUpdateRequest,
  Company,
  CompanyCreateRequest,
  CompanyUpdateRequest,
  DeviceNote,
  DeviceTimeline,
  ExcelImportResponse,
  ExportRowsResponse,
  SerialSwap,
  SessionUser,
  Shipment,
  SwapListResponse,
} from "@rma-orbit/shared";

/**
 * API 호출은 **전부 이 파일을 지난다.**
 *
 * 왜 한 곳에 모으는가: 화면 여기저기에 경로 문자열이 흩어지면, 서버 라우트가
 * 바뀌었을 때 어디가 깨지는지 아무도 모른다. 하네스 H1 ② 가 이 파일의 경로를
 * `apps/api/src/routes/manifest.ts` 와 대조한다 — 여기 없는 경로로 fetch 하면 FAIL.
 *
 * 프론트와 백엔드는 같은 오리진이다(docs/01 §1). 그래서 상대 경로만 쓴다.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
}

/**
 * 세션이 끊긴 것을 화면에 알린다.
 *
 * 세션은 12시간 슬라이딩이다(SECURITY.md §1). 대표가 화면을 켜 둔 채 퇴근하면 다음 날
 * 모든 호출이 401 이 되는데, 그걸 그냥 오류로 보여주면 **화면이 고장 난 것처럼 보인다.**
 * "다시 로그인하세요" 라고 말해야 사람이 다음 행동을 안다(SOUL S1 — 조용히 실패하지 않는다).
 */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // 세션 쿠키는 HttpOnly 다 — 브라우저가 알아서 싣는다
    credentials: "same-origin",
  });

  if (!response.ok) {
    let body: ErrorBody = {};
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      // 서버는 오류도 JSON 으로 준다. 아니라면 그것대로 알려야 한다
    }
    // 로그인 요청의 401 은 "비밀번호가 틀렸다" 지 "세션이 끊겼다" 가 아니다
    if (response.status === 401 && !path.startsWith("/api/auth/login")) {
      onSessionLost?.();
    }

    throw new ApiError(
      response.status,
      body.error ?? "UNKNOWN",
      body.message ?? `요청이 실패했습니다 (${response.status}).`,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function json(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export const api = {
  // --- 단일 진입점 (KNOWLEDGE-MAP.md §2) — 인증 없음, 구조만 ---
  manifest: () => call<RmaManifest>("/rma/manifest.json"),

  // --- auth (harness/API-SPEC.md §1) ---
  login: (email: string, password: string) =>
    call<SessionUser>("/api/auth/login", json("POST", { email, password })),
  logout: () => call<{ ok: true }>("/api/auth/logout", { method: "POST" }),
  me: () => call<SessionUser>("/api/auth/me"),

  // --- agent (§13-1, C85) ---
  agentAsk: (question: string, history: Array<{ role: "user" | "model"; text: string }>) =>
    call<AgentAskResponse>("/api/agent/ask", json("POST", { question, history })),

  // --- cases (§2) ---
  cases: (params: {
    status?: string;
    company?: string;
    serial?: string;
    from?: string;
    to?: string;
    limit?: number;
    cursor?: string;
      swap?: "any" | "none";
    approval_pending?: number;
}) => call<CaseListResponse>(`/api/cases${query(params)}`),
  frequentSymptoms: () => call<CaseSymptomsResponse>("/api/cases/symptoms"),
  caseDetail: (id: number) => call<CaseDetail>(`/api/cases/${id}`),
  updateCase: (id: number, patch: CaseUpdateRequest) =>
    call<CaseDetail>(`/api/cases/${id}`, json("PATCH", patch)),
  intake: (request: CaseBatchIntakeRequest) =>
    call<CaseBatchIntakeResponse>("/api/cases/batch", json("POST", request)),
  deleteCases: (caseIds: number[], reason: string) =>
    call<CaseDeleteResponse>("/api/cases/delete", json("POST", { case_ids: caseIds, reason })),

  // --- 달력·일정 (§2-5) ---
  calendar: (from: string, to: string) =>
    call<ScheduleListResponse>(`/api/schedules${query({ from, to })}`),
  createSchedule: (input: ScheduleCreateRequest) =>
    call<Schedule>("/api/schedules", json("POST", input)),
  deleteSchedule: (id: number) => call<{ ok: true }>(`/api/schedules/${id}`, { method: "DELETE" }),

  // --- devices (§3) ---
  device: (serial: string) => call<DeviceTimeline>(`/api/devices/${encodeURIComponent(serial)}`),
  addNote: (serial: string, body: string, category?: string) =>
    call<DeviceNote>(
      `/api/devices/${encodeURIComponent(serial)}/notes`,
      json("POST", { body, category }),
    ),

  // --- approvals (§4) ---
  approvalsOfCase: (caseId: number) =>
    call<ApprovalListResponse>(`/api/approvals${query({ case_id: caseId })}`),
  createApprovals: (caseIds: number[], kind: "SHIP_TO_VENDOR" | "SHIP_TO_OWNER") =>
    call<Approval[]>("/api/approvals", json("POST", { case_ids: caseIds, kind })),
  decideApprovals: (
    approvalIds: number[],
    decision: "APPROVED" | "REJECTED",
    decisionNote?: string,
  ) =>
    call<Approval[]>(
      "/api/approvals/decide",
      json("POST", { approval_ids: approvalIds, decision, decision_note: decisionNote }),
    ),

  // --- shipments (§5) ---
  shipBatch: (body: ShipmentBatchRequest) =>
    call<ShipmentBatchResponse>("/api/shipments/batch", json("POST", body)),
  createShipment: (input: {
    case_id: number;
    direction: "OUT_VENDOR" | "OUT_OWNER";
    dest_company_id: number;
    shipped_at: string;
    carrier?: string;
    tracking: string;
    approval_id: number;
  }) => call<Shipment>("/api/shipments", json("POST", input)),

  // --- swaps (§6) ---
  swaps: (params: { serial?: string; pending_only?: boolean }) =>
    call<SwapListResponse>(`/api/swaps${query(params)}`),
  createSwap: (body: SwapCreateRequest) =>
    call<SerialSwap>("/api/swaps", json("POST", body)),
  deleteSwap: (id: number, reason: string) =>
    call<void>(`/api/swaps/${id}`, json("DELETE", { reason })),
  confirmSwap: (id: number, note?: string) =>
    call<SerialSwap>(`/api/swaps/${id}/confirm`, json("POST", { note })),

  // --- export (§10) ---
  // --- api keys (§12) ---
  apiKeys: () => call<ApiKeyListResponse>("/api/admin/api-keys"),
  createApiKey: (name: string, scope: "read" | "ingest" | "admin") =>
    call<ApiKeyCreated>("/api/admin/api-keys", json("POST", { name, scope })),
  revokeApiKey: (id: number) =>
    call<ApiKey>(`/api/admin/api-keys/${id}`, { method: "DELETE" }),

  // --- evidence (§7) ---
  evidence: (params: { case_id?: number; serial?: string }) =>
    call<EvidenceListResponse>(`/api/evidence${query(params)}`),
  /** 원문을 그대로 올린다. 콘솔 로그는 붙여넣은 텍스트가 곧 파일이다 */
  uploadEvidence: (meta: EvidenceUploadMeta, file: File) => {
    const form = new FormData();
    form.set("payload", JSON.stringify(meta));
    form.set("file", file);
    return call<Evidence>("/api/evidence", { method: "POST", body: form });
  },
  evidenceUrl: (id: number) => `/api/evidence/${id}`,

  // --- mail (§9) ---
  mailDraft: (caseIds: number[], type: MailType) =>
    call<MailDraft>("/api/mail/draft", json("POST", { case_ids: caseIds, type })),

  exportCases: (params: {
    preset?: string;
    from?: string;
    to?: string;
    company?: string;
    status?: string;
    pii?: boolean;
  }) => call<ExportRowsResponse>(`/api/export/cases${query(params)}`),

  // --- import (§11) — multipart: payload + 원본 파일 ---
  importExcel: (payload: unknown, file: File) => {
    const form = new FormData();
    form.set("payload", JSON.stringify(payload));
    form.set("file", file);
    return call<ExcelImportResponse>("/api/import/excel", { method: "POST", body: form });
  },

  // --- admin (§12) ---
  users: () => call<User[]>("/api/admin/users"),
  createUser: (input: UserCreateRequest) => call<User>("/api/admin/users", json("POST", input)),
  auditLog: (params: { entity?: string; limit?: number; cursor?: string }) =>
    call<AuditLogResponse>(`/api/admin/audit-log${query(params)}`),
  harness: () => call<HarnessStatus[]>("/api/admin/harness"),
  crawlStatus: () => call<CrawlStatus>("/api/admin/crawl-status"),
  companies: () => call<Company[]>("/api/admin/companies"),
  createCompany: (input: CompanyCreateRequest) =>
    call<Company>("/api/admin/companies", json("POST", input)),
  updateCompany: (id: number, patch: CompanyUpdateRequest) =>
    call<Company>(`/api/admin/companies/${id}`, json("PATCH", patch)),
};
